/**
 * CommandPalette — Cmd/Ctrl-K launcher.
 *
 * Fuzzy-searches every app route plus a few quick actions and navigates on
 * select. Mounted once in the shell (DashboardLayout); it also registers the
 * global Cmd/Ctrl-K keydown listener that toggles it. Esc or backdrop closes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useUIStore, type Theme } from '@/stores/ui-store';

/** Non-navigation commands run an in-app effect instead of routing. */
type CommandAction = 'toggle-theme' | 'shortcuts';

interface Command {
  id: string;
  label: string;
  /** Target route (navigation commands). Omitted for `action` commands. */
  to?: string;
  group: 'Actions' | 'Navigation';
  hint?: string;
  keywords?: string;
  /** In-app effect for non-navigation commands. */
  action?: CommandAction;
}

const THEME_CYCLE: Theme[] = ['system', 'light', 'dark'];

const COMMANDS: Command[] = [
  // Quick actions first. `?new=1` is the create-intent flag (see
  // useCreateIntent): the target list page opens its create modal on land.
  { id: 'new-contact', label: 'Nouveau contact', to: '/crm/contacts?new=1', group: 'Actions', hint: 'CRM', keywords: 'créer lead prospect' },
  { id: 'new-company', label: 'Nouvelle entreprise', to: '/crm/companies?new=1', group: 'Actions', hint: 'CRM', keywords: 'créer société compte' },
  { id: 'new-content', label: 'Nouveau contenu', to: '/content/new', group: 'Actions', hint: 'Contenu', keywords: 'créer post publication rédiger' },
  { id: 'new-project', label: 'Nouveau projet', to: '/projects?new=1', group: 'Actions', hint: 'Projets', keywords: 'créer' },
  { id: 'new-campaign', label: 'Nouvelle campagne', to: '/outreach/campaigns?new=1', group: 'Actions', hint: 'Outreach', keywords: 'créer emailing séquence' },
  { id: 'new-design', label: 'Nouveau design', to: '/design', group: 'Actions', hint: 'Design', keywords: 'créer visuel graphique image' },
  { id: 'launch-agent', label: 'Lancer une tâche IA', to: '/ai-team', group: 'Actions', hint: 'IA', keywords: 'agent persona lancer exécuter tâche cmo community manager sdr designer' },
  { id: 'launch-orchestration', label: 'Orchestrer une équipe IA sur un objectif', to: '/orchestration', group: 'Actions', hint: 'IA', keywords: 'orchestration multi-agents plan dag objectif goal équipe lancer' },
  { id: 'activate-ai', label: 'Activer une capacité IA', to: '/ai-team', group: 'Actions', hint: 'IA', keywords: 'activer cmo virtuel content manager community manager équipe agents ia capacités studio' },
  { id: 'toggle-theme', label: 'Basculer le thème (clair / sombre)', group: 'Actions', hint: 'Affichage', keywords: 'thème theme sombre clair dark light apparence mode', action: 'toggle-theme' },
  { id: 'shortcuts', label: 'Raccourcis clavier', group: 'Actions', hint: 'Aide', keywords: 'aide help raccourcis touches keyboard clavier', action: 'shortcuts' },

  // Navigation — mirrors the sidebar and its sub-routes.
  { id: 'dashboard', label: 'Tableau de bord', to: '/', group: 'Navigation', keywords: 'accueil home dashboard' },
  { id: 'ai-ghostwriter', label: 'IA · Ghostwriter', to: '/ai/ghostwriter', group: 'Navigation', keywords: 'ghostwriter rédaction post long-form hook portée framework réécrire ia' },
  { id: 'analytics-monitoring', label: 'Analytics · Monitoring', to: '/analytics/monitoring', group: 'Navigation', keywords: 'monitoring alertes anomalies digests rrule surveillance métriques' },
  { id: 'crm-signals', label: 'CRM · Signaux', to: '/crm/signals', group: 'Navigation', keywords: 'signaux intention buying intent lead score feed règles' },
  { id: 'content', label: 'Contenu', to: '/content', group: 'Navigation', keywords: 'posts publications' },
  { id: 'calendar', label: 'Calendrier', to: '/calendar', group: 'Navigation', keywords: 'planning agenda' },
  { id: 'publishing', label: 'Publication', to: '/publishing', group: 'Navigation', keywords: 'publier files' },
  { id: 'publishing-history', label: 'Historique de publication', to: '/publishing/history', group: 'Navigation' },
  { id: 'platforms', label: 'Plateformes', to: '/platforms', group: 'Navigation', keywords: 'réseaux comptes connexions' },
  { id: 'ads', label: 'Publicités', to: '/ads', group: 'Navigation', keywords: 'ads publicités sponsoring campagnes payantes boost' },
  { id: 'media', label: 'Médiathèque', to: '/media-library', group: 'Navigation', keywords: 'images vidéos assets fichiers' },
  { id: 'bio', label: 'Link-in-bio', to: '/bio', group: 'Navigation', keywords: 'bio lien page profil linktree' },
  { id: 'analytics', label: 'Analytics', to: '/analytics', group: 'Navigation', keywords: 'statistiques stats métriques' },
  { id: 'analytics-overview', label: 'Analytics · Vue d’ensemble', to: '/analytics/overview', group: 'Navigation', keywords: 'aperçu synthèse insights' },
  { id: 'analytics-hashtags', label: 'Analytics · Hashtags', to: '/analytics/hashtags', group: 'Navigation', keywords: 'mots-clés tendances' },
  { id: 'analytics-competitors', label: 'Analytics · Concurrents', to: '/analytics/competitors', group: 'Navigation', keywords: 'veille benchmark' },
  { id: 'analytics-influencers', label: 'Analytics · Influenceurs', to: '/analytics/influencers', group: 'Navigation', keywords: 'créateurs ambassadeurs insights' },
  { id: 'analytics-funnels', label: 'Analytics · Funnels', to: '/analytics/funnels', group: 'Navigation', keywords: 'entonnoirs conversion étapes funnel' },
  { id: 'analytics-cohorts', label: 'Analytics · Rétention', to: '/analytics/cohorts', group: 'Navigation', keywords: 'cohortes rétention retention fidélisation' },
  { id: 'analytics-attribution', label: 'Analytics · Attribution / ROI', to: '/analytics/attribution', group: 'Navigation', keywords: 'attribution roi multi-touch retour investissement' },
  { id: 'analytics-goals', label: 'Analytics · Objectifs', to: '/analytics/goals', group: 'Navigation', keywords: 'objectifs goals okr kpi cibles' },
  { id: 'analytics-reports', label: 'Analytics · Rapports', to: '/analytics/reports', group: 'Navigation', keywords: 'exports insights' },
  { id: 'design', label: 'Design', to: '/design', group: 'Navigation', keywords: 'éditeur graphique visuels' },
  { id: 'design-brand-kits', label: 'Design · Kits de marque', to: '/design/brand-kits', group: 'Navigation', keywords: 'brand kit charte' },
  { id: 'workflows', label: 'Workflows', to: '/workflows', group: 'Navigation', keywords: 'automatisation flux orchestration scénarios' },
  { id: 'crm', label: 'CRM & Leads', to: '/crm', group: 'Navigation', keywords: 'prospection contacts' },
  { id: 'crm-contacts', label: 'CRM · Contacts', to: '/crm/contacts', group: 'Navigation', keywords: 'leads prospects' },
  { id: 'crm-companies', label: 'CRM · Entreprises', to: '/crm/companies', group: 'Navigation', keywords: 'sociétés comptes' },
  { id: 'crm-groups', label: 'CRM · Groupes', to: '/crm/groups', group: 'Navigation', keywords: 'segments listes' },
  { id: 'crm-pipeline', label: 'CRM · Pipeline', to: '/crm/pipeline', group: 'Navigation', keywords: 'ventes deals opportunités kanban' },
  { id: 'crm-dashboards', label: 'CRM · Tableaux de bord', to: '/crm/dashboards', group: 'Navigation', keywords: 'dashboards indicateurs kpi' },
  { id: 'crm-follow-ups', label: 'CRM · Relances', to: '/crm/follow-ups', group: 'Navigation', keywords: 'follow ups relances suivis tâches' },
  { id: 'crm-reminders', label: 'CRM · Rappels', to: '/crm/reminders', group: 'Navigation', keywords: 'reminders alertes échéances' },
  { id: 'crm-duplicates', label: 'CRM · Doublons', to: '/crm/duplicates', group: 'Navigation', keywords: 'duplicates fusion nettoyage' },
  { id: 'crm-scheduler', label: 'CRM · Rendez-vous', to: '/crm/scheduler', group: 'Navigation', keywords: 'scheduler rendez-vous réservation booking agenda' },
  { id: 'crm-forms', label: 'CRM · Formulaires', to: '/crm/forms', group: 'Navigation', keywords: 'forms formulaires web capture leads' },
  { id: 'crm-documents', label: 'CRM · Documents', to: '/crm/documents', group: 'Navigation', keywords: 'documents fichiers contrats signatures' },
  { id: 'crm-settings', label: 'CRM · Paramètres', to: '/crm/settings', group: 'Navigation', keywords: 'configuration réglages' },
  { id: 'prospection', label: 'Prospection · Découverte', to: '/prospection', group: 'Navigation', keywords: 'découverte discovery géo prospection google places opencorporates crunchbase apollo' },
  { id: 'prospection-recommend', label: 'Prospection · Recommandations', to: '/prospection/recommend', group: 'Navigation', keywords: 'recommandation ranking fit score prospects objectif goal produit classement' },
  { id: 'prospection-calendar', label: 'Prospection · Calendrier', to: '/prospection/calendar', group: 'Navigation', keywords: 'calendrier cadence multi-touch séquence prospection planning touches' },
  { id: 'account-intelligence', label: 'Prospection · Intelligence de compte', to: '/prospection/account-intelligence', group: 'Navigation', keywords: 'intelligence compte dossier analyse IA profil concurrent marché stratégie account intelligence' },
  { id: 'carousel-editor', label: 'Contenu · Éditeur de carrousel', to: '/content/carousel', group: 'Navigation', keywords: 'carrousel carousel diapositives slides export png pdf design contenu' },
  { id: 'rich-content-studio', label: 'Contenu · Studio de contenu riche', to: '/content/studio', group: 'Navigation', keywords: 'infographie infographic gif animé mp4 vidéo post-pack légende hashtags génération IA multimédia multi-plateforme linkedin' },
  { id: 'crm-activity', label: 'CRM · Fil d’activité', to: '/crm/activity', group: 'Navigation', keywords: 'activité activity feed collaboration partages attributions journal équipe' },
  { id: 'prospect', label: 'Base B2B', to: '/prospect', group: 'Navigation', keywords: 'prospection b2b annuaire enrichissement base entreprises' },
  { id: 'prospect-search', label: 'Base B2B · Recherche', to: '/prospect/search', group: 'Navigation', keywords: 'recherche b2b annuaire filtres entreprises contacts' },
  { id: 'prospect-lists', label: 'Base B2B · Mes listes', to: '/prospect/lists', group: 'Navigation', keywords: 'listes build a list segments prospection' },
  { id: 'marketplace', label: 'Marketplace', to: '/marketplace', group: 'Navigation', keywords: 'marketplace prestataires services réservations' },
  { id: 'marketplace-browse', label: 'Marketplace · Explorer', to: '/marketplace/browse', group: 'Navigation', keywords: 'explorer prestataires annuaire services' },
  { id: 'marketplace-provider', label: 'Marketplace · Espace prestataire', to: '/marketplace/dashboard', group: 'Navigation', keywords: 'prestataire fournisseur tableau de bord services' },
  { id: 'marketplace-bookings', label: 'Marketplace · Mes réservations', to: '/marketplace/my-bookings', group: 'Navigation', keywords: 'réservations bookings commandes' },
  { id: 'marketplace-reviews', label: 'Marketplace · Mes évaluations', to: '/marketplace/my-reviews', group: 'Navigation', keywords: 'évaluations avis notes reviews' },
  { id: 'social', label: 'Leads sociaux', to: '/social', group: 'Navigation', keywords: 'social leads réseaux prospects sociaux' },
  { id: 'listening', label: 'Écoute sociale', to: '/listening', group: 'Navigation', keywords: 'listening veille mentions social monitoring' },
  { id: 'outreach', label: 'Email Outreach', to: '/outreach', group: 'Navigation', keywords: 'emailing campagnes' },
  { id: 'outreach-campaigns', label: 'Outreach · Campagnes', to: '/outreach/campaigns', group: 'Navigation' },
  { id: 'outreach-sequences', label: 'Outreach · Séquences', to: '/outreach/sequences', group: 'Navigation' },
  { id: 'outreach-unibox', label: 'Outreach · Unibox', to: '/outreach/unibox', group: 'Navigation', keywords: 'unibox boîte unifiée réponses conversations' },
  { id: 'outreach-sending', label: 'Outreach · Délivrabilité', to: '/outreach/sending', group: 'Navigation', keywords: 'sending délivrabilité warmup expéditeurs domaines' },
  { id: 'outreach-templates', label: 'Outreach · Modèles', to: '/outreach/templates', group: 'Navigation' },
  { id: 'outreach-settings', label: 'Outreach · Paramètres', to: '/outreach/settings', group: 'Navigation', keywords: 'configuration expéditeur' },
  { id: 'inbox', label: 'Inbox sociale', to: '/inbox', group: 'Navigation', keywords: 'messages conversations' },
  { id: 'community', label: 'Community', to: '/community', group: 'Navigation', keywords: 'community productivité réponses macros sla csat campagnes influenceurs inbox' },
  { id: 'community-canned', label: 'Community · Réponses & macros', to: '/community/canned', group: 'Navigation', keywords: 'canned réponses enregistrées macros saved replies' },
  { id: 'community-sla', label: 'Community · SLA', to: '/community/sla', group: 'Navigation', keywords: 'sla délais politiques manquements breach' },
  { id: 'community-csat', label: 'Community · Satisfaction (CSAT)', to: '/community/csat', group: 'Navigation', keywords: 'csat satisfaction score enquête' },
  { id: 'community-campaigns', label: 'Community · Campagnes influenceurs', to: '/community/campaigns', group: 'Navigation', keywords: 'campagnes influenceurs influencer brief livrables' },
  { id: 'inbox-approvals', label: 'Inbox · Approbations', to: '/inbox/approvals', group: 'Navigation' },
  { id: 'inbox-automation', label: 'Inbox · Automatisation', to: '/inbox/automation', group: 'Navigation' },
  { id: 'projects', label: 'Projets', to: '/projects', group: 'Navigation', keywords: 'collaboration' },
  { id: 'ai-team', label: 'Équipe IA', to: '/ai-team', group: 'Navigation', keywords: 'agents personas cmo community manager designer sdr virtuel équipe activer lancer tâche capacités hub ia' },
  { id: 'orchestration', label: 'Orchestration IA', to: '/orchestration', group: 'Navigation', keywords: 'orchestration équipe multi-agents plan dag objectif goal graphe décision collective autonomie l1 l2 l3 l4' },
  { id: 'ai-team-builder', label: 'Constructeur d’équipe IA', to: '/ai-team/builder', group: 'Navigation', keywords: 'composer équipe agents sièges rôles comptable fiscaliste assistant nombre compétences outils autonomie l0 l5 multi-instance' },
  { id: 'ai-team-templates', label: 'Modèles d’équipe IA', to: '/ai-team/templates', group: 'Navigation', keywords: 'modèles templates équipe rôles réutilisable instancier plateforme admin' },
  { id: 'ai-team-pool', label: 'File de tâches IA', to: '/ai-team/pool', group: 'Navigation', keywords: 'file tâches pool assigner réclamer agent équipe non assignées' },
  { id: 'workflow-studio', label: 'Studio de flux (workflows)', to: '/workflows/studio', group: 'Navigation', keywords: 'studio workflow processus bpmn dmn décision glisser déposer drag drop nœuds diagramme visualisation exécution' },
  { id: 'ai-assistant', label: 'Assistant IA', to: '/ai-assistant', group: 'Navigation', keywords: 'copilot ia' },
  { id: 'ai-content-studio', label: 'IA · Studio de contenu', to: '/ai/content-studio', group: 'Navigation', keywords: 'carrousel thread post génération studio ia' },
  { id: 'ai-repurpose', label: 'IA · Repurposing', to: '/ai/repurpose', group: 'Navigation', keywords: 'recycler url article posts repurpose ia' },
  { id: 'ai-hook-library', label: 'IA · Bibliothèque de hooks', to: '/ai/hook-library', group: 'Navigation', keywords: 'accroches templates swipe few-shot hooks ia' },
  { id: 'ai-brand-voice', label: 'IA · Voix de marque', to: '/ai/brand-voice', group: 'Navigation', keywords: 'ton style profil score voix marque ia' },
  { id: 'ai-ab-copy', label: 'IA · Tests A/B copy', to: '/ai/ab-copy', group: 'Navigation', keywords: 'variantes ab test gagnante copy ia' },
  { id: 'ai-suggestions', label: 'IA · Suggestions', to: '/ai/suggestions', group: 'Navigation', keywords: 'recommandations analytics idées suggestions ia' },
  { id: 'ai-personalization', label: 'IA · Personnalisation', to: '/ai/personalization', group: 'Navigation', keywords: 'tokens crm merge fusion personnalisation ia' },
  { id: 'ai-community', label: 'IA · Community Manager', to: '/ai/community-ai', group: 'Navigation', keywords: 'réponses modération commentaires dm brouillon community ia' },
  { id: 'ai-rag-documents', label: 'IA · Documents RAG', to: '/ai/rag-documents', group: 'Navigation', keywords: 'rag documents recherche index pdf ia' },
  { id: 'ai-memory', label: 'IA · Mémoire', to: '/ai/memory', group: 'Navigation', keywords: 'mémoire préférences profil contexte memory ia' },
  { id: 'approvals', label: 'Approbations', to: '/approvals', group: 'Navigation', keywords: 'validation' },
  { id: 'ai-activity', label: 'Activité IA', to: '/ai-activity', group: 'Navigation', keywords: 'agents runs exécutions' },
  { id: 'settings', label: 'Paramètres', to: '/settings', group: 'Navigation', keywords: 'réglages configuration options' },
  { id: 'settings-profile', label: 'Paramètres · Profil', to: '/settings/profile', group: 'Navigation', keywords: 'thème compte' },
  { id: 'settings-credits', label: 'Paramètres · Crédits', to: '/settings/credits', group: 'Navigation' },
  { id: 'settings-ai-registry', label: 'Paramètres · Registre IA', to: '/settings/ai-registry', group: 'Navigation' },
  { id: 'settings-connections', label: 'Paramètres · Connexions', to: '/settings/connections', group: 'Navigation', keywords: 'intégrations comptes réseaux' },
];

function normalize(value: string): string {
  // Strip combining diacritics (U+0300–U+036F) so "sequences" matches "Séquences".
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Subsequence fuzzy score — lower is better; null when no match. Rewards
 * earlier and adjacent character matches.
 */
function fuzzyScore(query: string, text: string): number | null {
  const q = normalize(query);
  const t = normalize(text);
  if (q === '') return 0;

  let ti = 0;
  let score = 0;
  let prev = -2;
  for (const char of q) {
    let found = -1;
    for (; ti < t.length; ti += 1) {
      if (t[ti] === char) {
        found = ti;
        ti += 1;
        break;
      }
    }
    if (found === -1) return null;
    score += found;
    if (found === prev + 1) score -= 1; // adjacency bonus
    prev = found;
  }
  return score;
}

/**
 * Rank a command against the query (lower is better; null = no match).
 *
 * Tiers keep a real hit above keyword noise: a substring in the visible LABEL
 * always outranks a substring in hint/route/keywords, which always outranks a
 * loose subsequence. Before this, everything was one subsequence over the whole
 * concatenated haystack, so typing "calend" surfaced Calendrier but then smeared
 * in CRM Rappels / Analytics Hashtags / Ads because c-a-l-e-n-d happened to
 * appear scattered across their keywords.
 */
function scoreCommand(query: string, cmd: Command): number | null {
  const q = normalize(query.trim());
  if (q === '') return 0;
  const label = normalize(cmd.label);
  const hint = normalize(cmd.hint ?? '');
  const to = normalize(cmd.to ?? '');
  const keywords = normalize(cmd.keywords ?? '');

  // Tier 1 — substring of the label the user actually reads (prefix best).
  const li = label.indexOf(q);
  if (li !== -1) return li === 0 ? -1000 : -800 + li;
  // Tier 2 — substring of hint / route / keywords.
  const hi = hint.indexOf(q);
  if (hi !== -1) return -600 + hi;
  const oi = to.indexOf(q);
  if (oi !== -1) return -500 + oi;
  const ki = keywords.indexOf(q);
  if (ki !== -1) return -400 + ki;
  // Tier 3 — subsequence on the label only (typo tolerance), demoted below any
  // substring hit so keyword noise can never outrank a genuine label match.
  const fl = fuzzyScore(q, label);
  if (fl !== null) return 100 + fl;
  // Tier 4 — last-resort subsequence across the full haystack, demoted hard so
  // these scattered matches always sink beneath the real ones.
  const fh = fuzzyScore(q, `${label} ${hint} ${keywords} ${to}`);
  return fh === null ? null : 1000 + fh;
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global Cmd/Ctrl-K toggles the palette. Uses a ref so the listener stays
  // stable while still seeing the latest `open`.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!openRef.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenChange]);

  // Reset + focus each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setShowHelp(false);
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const results = useMemo(() => {
    if (query.trim() === '') return COMMANDS;
    return COMMANDS.map((cmd) => {
      const score = scoreCommand(query, cmd);
      return score === null ? null : { cmd, score };
    })
      .filter((x): x is { cmd: Command; score: number } => x !== null)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.cmd);
  }, [query]);

  useEffect(() => {
    setActiveIndex(0);
    if (query.trim() !== '') setShowHelp(false);
  }, [query]);

  if (!open) return null;

  const cycleTheme = () => {
    setTheme(THEME_CYCLE[(THEME_CYCLE.indexOf(theme) + 1) % THEME_CYCLE.length]);
  };

  const select = (cmd: Command | undefined) => {
    if (!cmd) return;
    if (cmd.action === 'shortcuts') {
      setShowHelp(true);
      return;
    }
    if (cmd.action === 'toggle-theme') {
      cycleTheme();
      onOpenChange(false);
      return;
    }
    if (cmd.to) {
      onOpenChange(false);
      void navigate(cmd.to);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // From the help view, Escape steps back to the results list first.
      if (showHelp) setShowHelp(false);
      else onOpenChange(false);
    } else if (showHelp) {
      // Help view has no list navigation.
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Defensive: activeIndex can momentarily point past a just-shrunk result
      // list (query typed then Enter before the reset effect commits). Clamp and
      // fall back to the top match so Enter always acts on a live command.
      select(results[activeIndex] ?? results[0]);
    }
  };

  const SHORTCUTS: { keys: string[]; label: string }[] = [
    { keys: ['⌘', 'K'], label: 'Ouvrir / fermer la palette' },
    { keys: ['/'], label: 'Rechercher (ouvrir la palette)' },
    { keys: ['?'], label: 'Aide des raccourcis clavier' },
    { keys: ['g', '…'], label: 'Aller à une section (g d, g c, …)' },
    { keys: ['↑', '↓'], label: 'Naviguer dans les résultats' },
    { keys: ['↵'], label: 'Ouvrir la sélection' },
    { keys: ['Esc'], label: 'Fermer (ou revenir en arrière)' },
  ];

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center bg-slate-950/60 p-4 pt-[12vh] backdrop-blur-sm"
      role="presentation"
      onMouseDown={() => onOpenChange(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Palette de commandes"
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-3"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 text-muted">
            <circle cx="11" cy="11" r="7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="m20 20-3.2-3.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une page ou une action…"
            aria-label="Rechercher"
            className="flex-1 bg-transparent py-3 text-sm text-foreground placeholder:text-muted focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted sm:inline">
            Esc
          </kbd>
        </div>

        {showHelp ? (
          <div className="max-h-80 overflow-y-auto p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
                Raccourcis clavier
              </span>
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="rounded-token px-2 py-1 text-xs text-muted hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Retour
              </button>
            </div>
            <ul className="space-y-1">
              {SHORTCUTS.map((s) => (
                <li
                  key={s.label}
                  className="flex items-center justify-between gap-3 rounded-token px-3 py-2 text-sm text-foreground"
                >
                  <span className="truncate">{s.label}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    {s.keys.map((k) => (
                      <kbd
                        key={k}
                        className="rounded border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-muted"
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
        <ul className="max-h-80 overflow-y-auto p-2" role="listbox" aria-label="Résultats">
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted">Aucun résultat.</li>
          )}
          {results.map((cmd, index) => {
            const active = index === activeIndex;
            const showHeader = index === 0 || results[index - 1].group !== cmd.group;
            return (
              <li key={cmd.id}>
                {showHeader && (
                  <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
                    {cmd.group}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(cmd)}
                  className={`flex w-full items-center justify-between gap-3 rounded-token px-3 py-2 text-left text-sm ${
                    active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-surface-2'
                  }`}
                >
                  <span className="truncate">{cmd.label}</span>
                  {cmd.hint && (
                    <span className={`shrink-0 text-xs ${active ? 'text-primary-foreground/80' : 'text-muted'}`}>
                      {cmd.hint}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        )}
      </div>
    </div>
  );
}
